import React from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Globe, Award, Users, Building2, Shield, TrendingUp, 
  CheckCircle, Linkedin, Mail, MapPin, ArrowRight, Star, ExternalLink
} from "lucide-react";

const AboutPage = () => {
  const navigate = useNavigate();

  const team = [
    {
      name: "Dr. Ross Rafahël",
      role: "Co-Founder, SKI Asia Pacific",
      bio: "Global governance researcher and systems designer with decades of experience across Europe, Middle East & South Africa. Architect of SKI's EU IPO-registered patented governance models.",
      expertise: ["Governance Innovation", "Systems Design", "Institutional Strategy"],
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face"
    },
    {
      name: "Dr. Neha Dewan",
      role: "Co-Founder & Regional Head",
      bio: "Expert in partnerships, institutional development, and impact-driven governance. Leads SKI Asia Pacific's expansion across Asia, the Middle East, and Africa.",
      expertise: ["Partnership Development", "Sustainability", "Future Economy"],
      email: "neha.dewan@skiasia.org",
      phone: "+91 800 688 0333",
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=300&h=300&fit=crop&crop=face"
    }
  ];

  const clients = [
    { name: "Air France–KLM", type: "Enterprise" },
    { name: "Royal Friesland Campina", type: "Enterprise" },
    { name: "SBM Offshore", type: "Enterprise" },
    { name: "National Treasuries", type: "Government" },
    { name: "Global Free Zones", type: "Government" }
  ];

  const stats = [
    { label: "Cross-Border Deals", value: "150+", icon: Globe, desc: "Global collaborations facilitated" },
    { label: "Compliance Rate", value: "98%", icon: Shield, desc: "Regulatory clarity across partnerships" },
    { label: "Assurance Checks", value: "500+", icon: CheckCircle, desc: "Partnership outcomes measured" },
  ];

  const offices = [
    { location: "SKI Asia Pacific FZCO", address: "IFZA Property FZCO, Building A1, Dubai Digital Park, Dubai Silicon Oasis, UAE", phone: "+971 52 965 0406" },
    { location: "SKI Asia BV", address: "The Netherlands", phone: "+31 682 165 722" }
  ];

  return (
    <div className="min-h-screen" style={{ background: '#FAF7F0', color: '#0D1B2A', fontFamily: "'DM Sans', sans-serif" }}>
      <Header />
      
      {/* Hero */}
      <div style={{ position: 'relative', padding: '90px 32px 64px', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(200,168,75,.025) 1px,transparent 1px),linear-gradient(90deg,rgba(200,168,75,.025) 1px,transparent 1px)', backgroundSize: '80px 80px' }} />
        <div style={{ position: 'absolute', top: '-20%', right: '-10%', width: '60%', height: '120%', background: 'radial-gradient(ellipse at 70% 40%,rgba(200,168,75,.08) 0%,transparent 60%)' }} />
        <div className="container mx-auto px-6 text-center" style={{ position: 'relative', zIndex: 5 }}>
          <Badge style={{ background: 'rgba(200,168,75,0.07)', border: '1px solid rgba(200,168,75,0.35)', color: '#C8A84B', fontSize: 11, fontWeight: 700, letterSpacing: '.22em', textTransform: 'uppercase', padding: '6px 16px', borderRadius: 50, marginBottom: 24 }}>Est. 2025 · Dubai & Netherlands</Badge>
          <h1 style={{ fontFamily: "'Cormorant Display', serif", fontSize: 'clamp(44px,6vw,76px)', fontWeight: 700, color: '#0D1B2A', marginBottom: 20, letterSpacing: '-.02em', lineHeight: 1 }}>
            About <em style={{ fontStyle: 'italic', color: '#C8A84B' }}>SKI Asia Pacific.</em>
          </h1>
          <p style={{ fontFamily: "'Cormorant Display', serif", fontSize: 21, fontWeight: 300, fontStyle: 'italic', color: '#3A4A5C', maxWidth: 760, margin: '0 auto', lineHeight: 1.55 }}>
            A global governance innovation company transforming how digital-first ecosystems scale, collaborate, and operate in the borderless economy.
          </p>
          <Button 
            onClick={() => window.open('https://www.skiasiapacific.com', '_blank')}
            style={{ marginTop: 32, background: '#C8A84B', color: '#07070A', fontWeight: 800, letterSpacing: '.16em', fontSize: 11, textTransform: 'uppercase', borderRadius: 50, padding: '14px 30px', border: 'none', height: 'auto' }}
          >
            Visit Official Website <ExternalLink className="h-3 w-3 ml-2" />
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12">
        
        {/* About Section */}
        <div className="max-w-4xl mx-auto mb-16">
          <Card style={{ background: '#FFFFFF', border: '1px solid rgba(200,168,75,0.35)', borderRadius: 0 }}>
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <Building2 className="h-7 w-7" style={{ color: '#C8A84B' }} />
                <h2 style={{ fontFamily: "'Cormorant Display', serif", fontSize: 32, fontWeight: 700, color: '#0D1B2A', letterSpacing: '-.01em' }}>Who we <em style={{ fontStyle: 'italic', color: '#C8A84B' }}>are.</em></h2>
              </div>
              <p style={{ color: '#3A4A5C', lineHeight: 1.65, marginBottom: 14, fontSize: 15 }}>
                <strong style={{ color: '#0D1B2A' }}>SKI Asia Pacific</strong> is a global governance innovation company transforming how digital-first ecosystems scale, collaborate, and operate in the borderless economy.
              </p>
              <p style={{ color: '#3A4A5C', lineHeight: 1.65, marginBottom: 14, fontSize: 15 }}>
                Registered in <strong style={{ color: '#0D1B2A' }}>The Netherlands (SKI Asia BV)</strong> and the <strong style={{ color: '#0D1B2A' }}>United Arab Emirates (SKI Asia Pacific FZCO)</strong>, we serve as the regional knowledge and governance hub for the Sustainable Knowledge Initiative across Asia, Europe, the Middle East, and Africa.
              </p>
              <p style={{ color: '#3A4A5C', lineHeight: 1.65, marginBottom: 24, fontSize: 15 }}>
                We combine <strong style={{ color: '#0D1B2A' }}>EU IPO-registered patented governance models</strong>, academic research, and decades of institutional experience to build future-ready governance architectures.
              </p>
              
              {/* Offices */}
              <div className="grid md:grid-cols-2 gap-4 p-5" style={{ background: 'rgba(200,168,75,0.05)', border: '1px solid rgba(200,168,75,0.3)' }}>
                {offices.map((office, i) => (
                  <div key={i}>
                    <p style={{ fontWeight: 700, color: '#C8A84B', fontSize: 13, letterSpacing: '.06em', marginBottom: 4 }}>{office.location}</p>
                    <p style={{ fontSize: 13, color: '#3A4A5C', lineHeight: 1.5 }}>{office.address}</p>
                    <p style={{ fontSize: 13, color: '#C8A84B', marginTop: 4 }}>{office.phone}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {stats.map((stat, i) => (
            <Card key={i} className="text-center" style={{ background: '#FFFFFF', border: '1px solid rgba(13,27,42,0.08)', borderRadius: 0 }}>
              <CardContent className="p-6">
                <stat.icon className="h-9 w-9 mx-auto mb-3" style={{ color: '#C8A84B' }} />
                <p style={{ fontFamily: "'Cormorant Display', serif", fontSize: 44, fontWeight: 700, color: '#0D1B2A', letterSpacing: '-.02em', lineHeight: 1 }}>{stat.value}</p>
                <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: '#C8A84B', marginTop: 8 }}>{stat.label}</p>
                <p style={{ fontSize: 13, color: '#3A4A5C', marginTop: 6, lineHeight: 1.5 }}>{stat.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Leadership Team */}
        <div className="mb-16">
          <h2 style={{ fontFamily: "'Cormorant Display', serif", fontSize: 'clamp(32px,4.4vw,52px)', fontWeight: 700, color: '#0D1B2A', textAlign: 'center', letterSpacing: '-.02em', lineHeight: 1.05, marginBottom: 8 }}>
            Our founding <em style={{ fontStyle: 'italic', color: '#C8A84B' }}>leadership.</em>
          </h2>
          <p style={{ textAlign: 'center', color: '#3A4A5C', fontFamily: "'Cormorant Display', serif", fontSize: 18, fontStyle: 'italic', fontWeight: 300, marginBottom: 36 }}>Decades of institutional experience across multiple continents</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {team.map((member, i) => (
              <Card key={i} className="overflow-hidden" style={{ background: '#FFFFFF', border: '1px solid rgba(13,27,42,0.08)', borderRadius: 0 }}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <img src={member.image} alt={member.name} className="w-20 h-20 rounded-full object-cover" style={{ border: '2px solid #C8A84B' }} />
                    <div>
                      <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0D1B2A' }}>{member.name}</h3>
                      <p style={{ color: '#C8A84B', fontWeight: 600, fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase', marginTop: 4 }}>{member.role}</p>
                    </div>
                  </div>
                  <p style={{ color: '#3A4A5C', fontSize: 14, marginBottom: 16, lineHeight: 1.65 }}>{member.bio}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {member.expertise?.map((exp, j) => (
                      <Badge key={j} style={{ background: 'rgba(200,168,75,0.07)', border: '1px solid rgba(200,168,75,0.35)', color: '#C8A84B', fontSize: 10, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase' }}>{exp}</Badge>
                    ))}
                  </div>
                  {member.email && (
                    <p style={{ fontSize: 13, color: '#3A4A5C' }}>
                      <Mail className="h-3 w-3 inline mr-1" style={{ color: '#C8A84B' }} /> {member.email}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Past Clients */}
        <div className="mb-16">
          <h2 style={{ fontFamily: "'Cormorant Display', serif", fontSize: 'clamp(28px,3.6vw,42px)', fontWeight: 700, color: '#0D1B2A', textAlign: 'center', letterSpacing: '-.02em', marginBottom: 8 }}>
            Past clients & <em style={{ fontStyle: 'italic', color: '#C8A84B' }}>experience.</em>
          </h2>
          <p style={{ textAlign: 'center', color: '#3A4A5C', fontFamily: "'Cormorant Display', serif", fontSize: 16, fontStyle: 'italic', fontWeight: 300, marginBottom: 36 }}>Our team has worked with globally recognised institutions</p>
          <div className="flex flex-wrap justify-center gap-4">
            {clients.map((client, i) => (
              <Card key={i} className="px-6 py-4" style={{ background: '#FFFFFF', border: '1px solid rgba(13,27,42,0.08)', borderRadius: 0 }}>
                <p style={{ fontWeight: 700, color: '#0D1B2A', fontSize: 14 }}>{client.name}</p>
                <p style={{ fontSize: 10, color: '#7A8190', letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 600, marginTop: 4 }}>{client.type}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA */}
        <Card className="text-center" style={{ background: '#FFFFFF', border: '1px solid rgba(200,168,75,0.3)', borderRadius: 0, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(to right, transparent, #C8A84B, transparent)' }} />
          <CardContent className="p-10">
            <h3 style={{ fontFamily: "'Cormorant Display', serif", fontSize: 'clamp(28px,4vw,44px)', fontWeight: 700, color: '#0D1B2A', marginBottom: 12, letterSpacing: '-.01em' }}>
              Ready to transform your <em style={{ fontStyle: 'italic', color: '#C8A84B' }}>governance?</em>
            </h3>
            <p style={{ color: '#3A4A5C', marginBottom: 28, fontFamily: "'Cormorant Display', serif", fontStyle: 'italic', fontSize: 17, fontWeight: 300 }}>Join the SKI AXIS Accelerator and build with integrity.</p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Button onClick={() => navigate('/apply')} style={{ background: '#C8A84B', color: '#07070A', fontWeight: 800, letterSpacing: '.18em', fontSize: 11, textTransform: 'uppercase', borderRadius: 50, padding: '14px 28px', height: 'auto', border: 'none' }}>
                Apply Now <ArrowRight className="h-3 w-3 ml-2" />
              </Button>
              <Button onClick={() => navigate('/media')} style={{ background: 'transparent', color: '#0D1B2A', fontWeight: 600, letterSpacing: '.14em', fontSize: 11, textTransform: 'uppercase', borderRadius: 50, padding: '14px 28px', height: 'auto', border: '1px solid rgba(200,168,75,0.3)' }}>
                Read Press Coverage
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default AboutPage;
